import React from 'react';
import { Crown, Gem, Feather, Lightbulb, Music, Palette } from 'lucide-react';
import { Link } from 'react-router-dom';
import img1 from "../../assets/interior1.jpg";
import img2 from "../../assets/interior2.jpg";
import img3 from "../../assets/interior3.jpg";
import img4 from "../../assets/interior4.jpg";
import img5 from "../../assets/interior5.jpg";

const designs = [
  { 
    id: 1, 
    title: 'Grand Foyers', 
    category: 'Entrance', 
    image: img1,
    desc: 'Double-height ceilings with crystal chandeliers.'
  },
  { 
    id: 2, 
    title: 'Royal Dining Halls', 
    category: 'Dining', 
    image: img2,
    desc: '12-seater onyx tables with gold inlay work.' 
  },
  { 
    id: 3, 
    title: 'Private Home Theaters', 
    category: 'Entertainment', 
    image: "https://images.unsplash.com/photo-1595769816263-9b910be24d5f?auto=format&fit=crop&q=80",
    desc: 'Recliner seating, 4K projection, and acoustic walls.' 
  },
  { 
    id: 4, 
    title: 'Spiritual Mandir Rooms', 
    category: 'Divine', 
    image: "https://www.99acres.com/microsite/articles/files/2023/05/Stunning-traditional-mandir-design_pinterest_Abstract-Design-Studio.jpg",
    desc: 'Vastu-compliant marble mandirs with intricate carving.'
  },
  { 
    id: 5, 
    title: 'Staircase Elegance', 
    category: 'Architecture', 
    image: img4,
    desc: 'Spiral staircases with wrought iron railings.'
  },
  { 
    id: 6, 
    title: 'Bespoke Art Walls', 
    category: 'Decor', 
    image: img3,
    desc: 'Hand-painted murals and texture paint finishes.' 
  },
];

const LuxuryInteriors = () => {
  return (
    <div className="font-sans text-gray-800 bg-white">
      
      {/* 1. HERO SECTION */}
       <div
        className="relative h-[70vh] w-full bg-cover bg-center"
        style={{ backgroundImage: `url(${img5})` }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 animate-fadeIn">
          <h4 className="text-amber-500 font-bold tracking-[0.3em] uppercase mb-3">
            Beyond Ordinary
          </h4>
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-4">
            Royal Home Interiors
          </h1>
          <p className="text-gray-200 text-lg md:text-xl max-w-2xl font-light">
            Curating timeless elegance for estates and villas in <strong>Lucknow, Varanasi & Delhi</strong>.
          </p>
        </div>
      </div>

      {/* 2. INTRO SECTION */}
      <section className="py-20 px-6 bg-white text-center">
        <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
           Grandeur & Grace
        </h2>
        <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
        <p className="text-gray-600 max-w-4xl mx-auto leading-loose text-lg">
          True luxury is in the details. From breathtaking chandeliers to hand-carved moldings, our services cover the full spectrum of home design. 
          We specialize in Turnkey Projects, meaning we handle everything from civil work to the final cushion placement. 
          We focus on flow, light, and texture to create homes in Varanasi and Noida that feel like modern palaces.
        </p>
      </section>

      {/* 3. EXPANDED GALLERY */}
      <section className="py-20 bg-neutral-50 px-6">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-4xl font-serif font-bold text-gray-900 mb-12 text-center">
             Signature Spaces
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {designs.map((design) => (
              <div key={design.id} className="group relative overflow-hidden shadow-lg cursor-pointer rounded-sm">
                <div className="h-80 overflow-hidden">
                  <img 
                    src={design.image} 
                    alt={`${design.title} - Luxury Interior Design`} 
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition duration-700"
                  />
                </div>
                <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/90 to-transparent p-6 pt-20">
                  <p className="text-amber-400 text-xs tracking-widest uppercase mb-1">{design.category}</p>
                  <h4 className="text-2xl font-serif text-white mb-1">{design.title}</h4>
                  <p className="text-gray-300 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300">{design.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. FEATURES & DETAILS (NEW) */}
      <section className="py-24 px-6 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto">
           <div className="text-center mb-16">
              <h3 className="text-3xl font-serif font-bold text-white">The Elements of Luxury</h3>
           </div>
           
           <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
              <div className="flex flex-col items-center">
                <Crown className="w-12 h-12 text-amber-500 mb-6" />
                <h4 className="text-xl font-serif font-bold mb-3">Royal Aesthetics</h4>
                <p className="text-gray-400 leading-relaxed">
                   Inspired by Indian palaces. We use gold-leafing, velvet upholstery, and intricate jaali work typical of Lucknow heritage.
                </p>
              </div>
              <div className="flex flex-col items-center">
                <Lightbulb className="w-12 h-12 text-amber-500 mb-6" />
                <h4 className="text-xl font-serif font-bold mb-3">Lighting Design</h4>
                <p className="text-gray-400 leading-relaxed">
                   We layer lighting with chandeliers, cove lights, and wall sconces to create drama and warmth in every room.
                </p>
              </div>
              <div className="flex flex-col items-center">
                <Gem className="w-12 h-12 text-amber-500 mb-6" />
                <h4 className="text-xl font-serif font-bold mb-3">Detail Oriented</h4>
                <p className="text-gray-400 leading-relaxed">
                   From flooring patterns (Italian Marble inlays) to POP ceiling cornices, no corner is left untouched.
                </p>
              </div>
              
              {/* Row 2 */}
              <div className="flex flex-col items-center mt-8">
                <Feather className="w-12 h-12 text-amber-500 mb-6" />
                <h4 className="text-xl font-serif font-bold mb-3">Bespoke Decor</h4>
                <p className="text-gray-400 leading-relaxed">Custom-made art pieces, rugs, and curtains sourced from global artisans.</p>
              </div>
              <div className="flex flex-col items-center mt-8">
                <Music className="w-12 h-12 text-amber-500 mb-6" />
                <h4 className="text-xl font-serif font-bold mb-3">Entertainment</h4>
                <p className="text-gray-400 leading-relaxed">Soundproofing and acoustics for media rooms and lounges.</p>
              </div>
              <div className="flex flex-col items-center mt-8">
                <Palette className="w-12 h-12 text-amber-500 mb-6" />
                <h4 className="text-xl font-serif font-bold mb-3">Vastu Compliant</h4>
                <p className="text-gray-400 leading-relaxed">Designs that align with cosmic energy for health and prosperity.</p>
              </div>
           </div>
        </div>
      </section>

      {/* 5. CTA */}
      <section className="py-24 px-6 text-center bg-white">
        <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
           Create Your Masterpiece
        </h2>
        <p className="text-gray-600 mb-10 text-lg">Schedule a site visit with our senior architects today.</p>
        <Link to="/contact" className="inline-block bg-amber-500 hover:bg-amber-600 text-white text-lg font-bold px-10 py-4 rounded-sm transition shadow-lg uppercase tracking-widest">
           Get Premium Quote
        </Link>
      </section>
    </div>
  );
};

export default LuxuryInteriors;